import { unitsLib } from 'src/app/core/services/unit-library';

export type UnitPrecisionMap = Readonly<Record<string, number>>;
export type UnitPrecisionOverrides = Readonly<Partial<Record<string, number>>>;

const DEFAULT_DECIMALS_FOR_KNOWN_UNITS = 2;

// Mirrors canonical backend enum VALUE strings from Perifor.Domain.Enums.UnitEnums.
// These are the strings validated by UserUnits.Builder.ValidateUnit(...) in backend.
const BACKEND_CANONICAL_UNIT_VALUES: readonly string[] = [
    'ft', 'm',
    'in', 'cm', 'mm',
    'd-m', 'd-ft', 'd-100m',
    '∆°C/m', '∆°F/ft', '∆°C/km',
    'kg/m', 'lb/ft', 'lb/in', 'g/m', 'g/cm',
    '°C', '°F', 'K',
    'bar', 'psi', 'KPa', 'atm',
    'tf', 'kgf', 'lbf', 'kip',
    'm/s', 'ft/s',
    'kg/m³', 'ppg (U.S.)', 'g/m³', 'g/cm³', 'g/m³', 'g/L', 'kg/l', 'sg',
    'lbm/ft³',
    'm³/min', 'GPM', 'bpm', 'LPM',
    'm³/d', 'bbl/d',
    'bbl/min',
    'm³', 'bbl', 'l',
    'cm²', 'in²', 'ft²', 'm²',
    'W/m·K', 'BTU/h·ft·°F',
    '1/°C', '1/°F',
    'Sm³/h', 'scfh', 'Sm³/hBase', 'scfhBase',
    'lb/h', 't/d', 'kg/s',
    'BTU/ft³·°F', 'J/m³·°C',
    'kPa/m', 'bar/m', 'psi/ft',
    'BTU/lbm', 'J/kg',
    'BTU', 'J',
    'lbf·ft', 'N·m',
    'Btu/ft²·°F', 'W/m²·K',
    'psi/100ft', 'bar/1000m',
    'hp(I)', 'Btu/h', 'W', 'J/h',
    'lbf/100ft²', 'Pa',
    'cP', 'Pa/s'
];

const UNIT_PRECISION_OVERRIDES: UnitPrecisionMap = Object.freeze({
    m: 3,
    'degf': 1,
    'degc': 1,
    'degc/m': 3,
    'degc/30m': 3,
    'degc/100m': 3,
    'kpa': 1,
    'mpa': 3
});

export function normalizeUnitSymbol(unit: string): string {
    return unit
        .toLowerCase()
        .replace('°', 'deg')
        .replace('∆', '')
        .replace(/\s+/g, '');
}

function normalizePrecisionMap(map: Readonly<Partial<Record<string, number>>>): Record<string, number> {
    const normalizedMap: Record<string, number> = {};

    Object.entries(map).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedMap[normalizeUnitSymbol(key)] = value;
        }
    });

    return normalizedMap;
}

function buildDefaultUnitPrecisionMap(): UnitPrecisionMap {
    const knownUnits = new Set<string>();

    Object.keys(unitsLib).forEach((unit) => {
        knownUnits.add(normalizeUnitSymbol(unit));
    });

    BACKEND_CANONICAL_UNIT_VALUES.forEach((unit) => {
        knownUnits.add(normalizeUnitSymbol(unit));
    });

    Object.values(unitsLib).forEach((unitDef: any) => {
        if (unitDef?.symbol) {
            knownUnits.add(normalizeUnitSymbol(unitDef.symbol));
        }
    });

    const defaults = Object.fromEntries(
        Array.from(knownUnits)
            .filter((unit) => !!unit)
            .map((unit) => [unit, DEFAULT_DECIMALS_FOR_KNOWN_UNITS])
    );

    return Object.freeze({
        ...defaults,
        ...UNIT_PRECISION_OVERRIDES
    });
}

export const DEFAULT_UNIT_PRECISION_MAP: UnitPrecisionMap = buildDefaultUnitPrecisionMap();

export function resolveUnitPrecision(
    unit: string | null | undefined,
    unitPrecisionMap: UnitPrecisionMap = DEFAULT_UNIT_PRECISION_MAP,
    unitPrecisionOverrides: UnitPrecisionOverrides = {}
): number {
    const normalizedPrecisionOverrides = normalizePrecisionMap(unitPrecisionOverrides);
    const normalizedUnit = unit ? normalizeUnitSymbol(unit) : '';

    if (normalizedUnit === '' && normalizedPrecisionOverrides[''] !== undefined) {
        return normalizedPrecisionOverrides[''];
    }

    if (!unit) {
        return DEFAULT_DECIMALS_FOR_KNOWN_UNITS;
    }

    const normalizedPrecisionMap = normalizePrecisionMap(unitPrecisionMap);

    return (
        normalizedPrecisionOverrides[normalizedUnit] ??
        normalizedPrecisionMap[normalizedUnit] ??
        DEFAULT_DECIMALS_FOR_KNOWN_UNITS
    );
}
